import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { useFilters } from '@components/v2/dateFilter/hooks/useFilters'
import { getDailyIntegrationsReport } from '@global/api'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { DailyActivity } from '@global/api/types'

interface DailyActivityWithIntegrationNameAndSlug extends DailyActivity {
  integrationName: string
  integrationSlug: string
}

interface Integration {
  id: number
  type: string
  name: string
  slug: string
  description: string
  powered_by_connector_type_id: number
  created_at: string | null
  updated_at: string | null
  active: 0 | 1
  long_description: string
  globally_available: 0 | 1
}

interface IntegrationSummary {
  integrationId: number
  totalRowCount: number
  integrationName: string | undefined
  integrationSlug: string | undefined
}

function formatAndFilterData(
  data: DailyActivity[],
  integrationsById: Record<number, Integration>,
): DailyActivityWithIntegrationNameAndSlug[] {
  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

  return (
    data
      .filter(item => item.integrationId)
      .map(item => ({
        ...item,
        date: new Date(item.date),
        integrationName: integrationsById[item.integrationId]?.name ?? '',
        integrationSlug: integrationsById[item.integrationId]?.slug ?? '',
      }))
      // Filter out data older than 6 months
      .filter(item => item.date >= sixMonthsAgo)
  )
}

function calculateReportsGroupedByIntegrationId(
  filteredData: DailyActivityWithIntegrationNameAndSlug[],
): IntegrationSummary[] {
  const summariesMap = filteredData.reduce<Record<number, IntegrationSummary>>((acc, item) => {
    const totalRowCount = acc[item.integrationId]?.totalRowCount ?? 0

    acc[item.integrationId] = {
      integrationId: item.integrationId,
      totalRowCount: totalRowCount + item.rowCount,
      integrationName: item.integrationName,
      integrationSlug: item.integrationSlug,
    }
    return acc
  }, {})

  return Object.values(summariesMap).sort((a, b) => b.totalRowCount - a.totalRowCount)
}

function getLatestSumissionDate(filteredData) {
  if (filteredData.length === 0) return null
  return new Date(Math.min(...filteredData.map(item => item.date.getTime())))
}

function mapIntegrationData(data: DailyActivity[], integrationsById: Record<number, Integration>) {
  try {
    const filteredData = formatAndFilterData(data, integrationsById)
    const summaries = calculateReportsGroupedByIntegrationId(filteredData)
    const latestSubmissionDate = getLatestSumissionDate(filteredData)

    return {
      latestSubmissionDate,
      summaries,
    }
  } catch (error) {
    console.log('Error mapping integration data', error)
  }
}

const getIntegrationsWithAndWithoutData = (integrations: IntegrationSummary[] | undefined) => {
  const integrationsWithData = integrations?.filter(integration => integration.totalRowCount > 0)
  const integrationsWithoutData = integrations?.filter(
    integration => !integration.totalRowCount || integration.totalRowCount === 0,
  )

  return [integrationsWithData, integrationsWithoutData]
}

const buildDates = numberOfDaysToFilter => ({
  to: moment().format('YYYY-MM-DD'),
  from: moment().subtract(numberOfDaysToFilter, 'days').format('YYYY-MM-DD'),
})

export function useIntegrationsReport() {
  const { id: accountId } = useAppSelector(state => state.Auth.account)
  const { numberOfDaysToFilter } = useFilters()
  const { from, to } = buildDates(numberOfDaysToFilter)

  const { data: activeIntegrations, isFetched: isFetchedActiveIntegrations } = useQuery({
    queryKey: ['active-integrations', accountId],
    queryFn: async () => {
      const response = await api.get('/api/v1/integrations/active')
      return response.data
    },
    refetchOnWindowFocus: false,
  })

  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    isFetched: isFetchedIntegrations,
  } = useQuery({
    queryKey: ['all-integrations', accountId],
    queryFn: async () => {
      const response = await api.get('/api/v1/integrations/query')
      return response.data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(isFetchedActiveIntegrations && activeIntegrations),
    select: data => ({
      byId: data.reduce((acc, integration) => {
        acc[integration.id] = integration
        return acc
      }, {}),
      active: data.filter(integration =>
        activeIntegrations.some(activeIntegration => activeIntegration.id === integration.id),
      ) as Integration[],
    }),
  })

  const { data: integrationsReportData, isLoading: isLoadingDailyReport } = useQuery({
    queryKey: ['integrations-graph', accountId, from, to],
    queryFn: ({ signal }) => getDailyIntegrationsReport({ signal, params: { from, to } }),
    select: data => {
      const allInboundIntegrations = mapIntegrationData(data.submissions, integrations?.byId)
      const allOutboundIntegrations = mapIntegrationData(data.outbounds, integrations?.byId)

      /**
       * Get the highlighted integrations and leftovers
       */
      const [inboudIntegrationsWithData, inboundIntegrationsWithoutData] =
        getIntegrationsWithAndWithoutData(allInboundIntegrations?.summaries)
      const [outboundsIntegrationsWithData, outboundsIntegrationsWithoutData] =
        getIntegrationsWithAndWithoutData(allOutboundIntegrations?.summaries)

      return {
        submissions: {
          all: allInboundIntegrations,
          withData: inboudIntegrationsWithData,
          withoutData: inboundIntegrationsWithoutData,
        },
        outbounds: {
          all: allOutboundIntegrations,
          withData: outboundsIntegrationsWithData,
          withoutData: outboundsIntegrationsWithoutData,
        },
      }
    },
    enabled: Boolean(isFetchedIntegrations && integrations),
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    cacheTime: 1000 * 60 * 60 * 24, // 1 day
  })

  // get the latestSubmissionDate from submissions and outbounds
  const latestSubmissionDate = new Date(
    Math.min(
      integrationsReportData?.submissions?.all?.latestSubmissionDate?.getTime() || 0,
      integrationsReportData?.outbounds?.all?.latestSubmissionDate?.getTime() || 0,
    ),
  )

  const formattedLatestSubmissionDate = latestSubmissionDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })

  /**
   * Get all the integrations that are not used in the last 6 months
   * and are not in the highlighted integrations, we treat them as unused integrations
   * because we cannot show all used integrations in the graph sometimes
   */
  const legacyIntegrations = ['Legacy Facebook Ads Manager', 'Legacy Google CM360', 'Email Send'] // Example legacy integrations
  const integrationsWithData = [
    ...(integrationsReportData?.submissions?.withData || []),
    ...(integrationsReportData?.outbounds?.withData || []),
  ]

  const unusedIntegrations = integrations?.active
    ?.filter(
      // Filter out integrations with data
      integration =>
        !integrationsWithData.some(
          integrationData => integrationData.integrationId === integration.id,
        ) && !legacyIntegrations.includes(integration.name), // Filter out legacy integrations by name
    )
    .reduce(
      (unique: Integration[], item: Integration) =>
        unique.some(integration => integration.name === item.name) ? unique : [...unique, item],
      [],
    )

  return {
    submissions: integrationsReportData?.submissions,
    outbounds: integrationsReportData?.outbounds,
    unusedIntegrations,
    latestSubmissionDate: formattedLatestSubmissionDate,
    isLoading: isLoadingIntegrations || isLoadingDailyReport,
  }
}
